import type { JsonSchema, JsonValue, MapDocument, ValidationIssue, WorldDocument } from "daedalus-core";
import { CHEST } from "../editor/type-names.ts";
import { isPlainRecord } from "../rules/checks.ts";
import type { Rules } from "../rules/types.ts";
import { shortCoins } from "../currency/coins.ts";
import { isCoinEntry, lootEntryData, validateLootTable, type LootTable } from "./loot.ts";

/**
 * The chest as content (ADR-091 decisions 17 and 18): the field a chest prop names its loot table by, the Issues a
 * chest raises, the chests of a world as the compile walks them, and the compiled tables a version carries for its
 * map server (`server/loot.json`), written and read here so the compile and the map server agree on the file. Pure.
 */

/**
 * The name of the list a chest's loot table is chosen from in the editor: the loot tables the library holds, which
 * the editor page hands the store under this name.
 */
export const LOOT_LOOKUP = "loot-tables";

/**
 * The chance, in whole percent, of an entry that always gives.
 */
const CERTAIN = 100;

/**
 * The name of the field holding the table's id.
 */
const LOOT_FIELD = "loot";

/**
 * A chest's fields as the editor's inspector draws them.
 */
export const CHEST_SCHEMA: JsonSchema = { type: "object", properties: { [LOOT_FIELD]: { type: "string", title: "Loot table", lookup: LOOT_LOOKUP } }, required: [LOOT_FIELD] };

/**
 * One chest of a world: its map, its prop's authored id and the table it names, null when it names none.
 */
export interface WorldChest {
  readonly mapId: string;
  readonly propId: string;
  readonly loot: string | null;
}

/**
 * The loot table a chest names.
 *
 * @param fields The chest's behaviour fields.
 * @returns The table's id, or null when the field is missing, empty or not text.
 */
export function chestLoot(fields: { readonly [key: string]: JsonValue }): string | null {
  const value = fields[LOOT_FIELD];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * The Issues a map's chests raise in the editor and at a compile: one refusal for each chest that names no loot
 * table, with its fix after the semicolon. A table the library no longer holds is the lookup's own Issue, raised by
 * the editor once it knows the list, and the compile's refusal when it reads the table.
 *
 * @param map The map.
 * @returns The issues, in document order.
 */
export function chestIssues(map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type === CHEST && chestLoot(prop.behaviour.fields) === null) {
        issues.push({ mapId: map.id, kind: "chest.no-loot", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is a Chest that names no loot table; choose its Loot table` });
      }
    }
  }
  return issues;
}

/**
 * Every chest of a world, map by map and layer by layer in document order.
 *
 * @param world The world document.
 * @returns The chests.
 */
export function worldChests(world: WorldDocument): WorldChest[] {
  const chests: WorldChest[] = [];
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        if (prop.behaviour?.type === CHEST) {
          chests.push({ mapId: map.id, propId: prop.id, loot: chestLoot(prop.behaviour.fields) });
        }
      }
    }
  }
  return chests;
}

/**
 * The text of a version's compiled loot: `{ "tables": [...] }`, the tables sorted by id so the same set gives the
 * same bytes, and so the same version label, whatever order the world's chests name them in.
 *
 * @param tables Tables `validateLootTable` admitted, each id once.
 * @returns The file's text, ending in a newline.
 */
export function serialiseCompiledLoot(tables: readonly LootTable[]): string {
  const sorted = [...tables].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return `${JSON.stringify({ tables: sorted.map((table) => ({ id: table.id, name: table.name, entries: table.entries.map(lootEntryData) })) }, null, 2)}\n`;
}

/**
 * Reads and checks a version's compiled loot, as a map server loads it: every table is checked again against the
 * rules the reader runs, so a version compiled under other rules is refused at load and not at a roll.
 *
 * @param value The parsed file.
 * @param rules The rules whose items the tables name.
 * @returns The tables, in the file's order.
 * @throws Error when the file is not `{ tables: [...] }`, holds another field, holds a table twice, or a table fails
 *   `validateLootTable`, naming every error of the first table that fails.
 */
export function parseCompiledLoot(value: unknown, rules: Rules): LootTable[] {
  if (!isPlainRecord(value) || !Array.isArray(value["tables"])) {
    throw new Error("the compiled loot must be { tables: [...] }");
  }
  for (const key of Object.keys(value)) {
    if (key !== "tables") {
      throw new Error(`${key} is not a field of the compiled loot`);
    }
  }
  const tables: LootTable[] = [];
  const seen = new Set<string>();
  for (const [index, entry] of (value["tables"] as readonly unknown[]).entries()) {
    const id = isPlainRecord(entry) && typeof entry["id"] === "string" ? entry["id"] : `tables[${index}]`;
    const verdict = validateLootTable(entry, id, rules);
    if (!verdict.ok) {
      throw new Error(verdict.errors.join("\n"));
    }
    if (seen.has(verdict.table.id)) {
      throw new Error(`the compiled loot holds ${verdict.table.id} twice`);
    }
    seen.add(verdict.table.id);
    tables.push(verdict.table);
  }
  return tables;
}

/**
 * What a loot table holds, in words, as the editor's Loot table list shows it beside the table's name: each entry's
 * quantity and its item's name in the rules, or a coin entry's copper in the short form, with its chance when it is not
 * certain.
 *
 * @param table The table.
 * @param rules The rules whose items name the entries; an item they do not hold is named by its id.
 * @returns The entries, joined by commas.
 * @example
 * describeLootContents(starterChest, loadRules());
 * // "1 to 3 Minor Healing Potion"
 * describeLootContents(ratDrops, loadRules());
 * // "4b to 10b, 1 Minor Healing Potion (4%)"
 */
export function describeLootContents(table: LootTable, rules: Rules): string {
  return table.entries
    .map((entry) => {
      const chance = entry.chance === CERTAIN ? "" : ` (${entry.chance}%)`;
      if (isCoinEntry(entry)) {
        const [min, max] = entry.coin;
        return `${min === max ? shortCoins(min) : `${shortCoins(min)} to ${shortCoins(max)}`}${chance}`;
      }
      const quantity = entry.min === entry.max ? `${entry.min}` : `${entry.min} to ${entry.max}`;
      return `${quantity} ${rules.items.get(entry.item)?.name ?? entry.item}${chance}`;
    })
    .join(", ");
}
